import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Trash2, RotateCcw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import RiapriPraticaDialog from "@/components/RiapriPraticaDialog";

/** Sprint 4Z.2 B3 — Modal Cestino unificato.
 *  Props:
 *    open, onOpenChange
 *    tipo: "preventivo" | "pratica"
 *    isAdmin: boolean (mostra "Svuota cestino" e "Elimina definitivamente")
 *    onChange: callback dopo azione (per ricaricare la lista padre)
 */
export default function CestinoModal({ open, onOpenChange, tipo, isAdmin, onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirmEmpty, setConfirmEmpty] = useState(false);
  const [riapri, setRiapri] = useState(null);

  const titleLabel = tipo === "preventivo" ? "Cestino Preventivi" : "Cestino Pratiche";
  const itemNoun = tipo === "preventivo" ? "preventivo" : "pratica";

  const load = async () => {
    if (!open) return;
    setLoading(true);
    try {
      const r = await api.get(`/vendite/cestino?tipo=${tipo}`);
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error("Errore caricamento cestino");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tipo]);

  const handleRipristina = async (it) => {
    setBusyId(it.id);
    try {
      if (tipo === "pratica" && it.status === "annullata" && !it.deleted_at) {
        // F3: pratica annullata → dialog che chiede il cliente se azzerato (Sprint 3)
        setRiapri(it);
        return;
      } else {
        await api.post(`/vendite/${it.id}/ripristina`, { stato_partenza: "cestinata" });
        toast.success(`${itemNoun.charAt(0).toUpperCase() + itemNoun.slice(1)} ${it.codice} ripristinato`);
      }
      load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore ripristino");
    } finally {
      setBusyId(null);
    }
  };

  const handlePermanentDelete = async (it) => {
    if (!window.confirm(`Eliminare DEFINITIVAMENTE ${it.codice}? L'operazione NON è reversibile.`)) return;
    setBusyId(it.id);
    try {
      await api.delete(`/vendite/${it.id}/permanent`);
      toast.success(`${it.codice} eliminato definitivamente`);
      load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    } finally {
      setBusyId(null);
    }
  };

  const handleSvuota = async () => {
    setBusyId("__empty__");
    try {
      const r = await api.post("/vendite/cestino/empty", { tipo });
      toast.success(`Cestino svuotato: ${r.data?.deleted_count || 0} elementi rimossi`);
      setConfirmEmpty(false);
      load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore svuotamento");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="cestino-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5" /> {titleLabel}
          </DialogTitle>
          <DialogDescription>
            {tipo === "preventivo"
              ? "Preventivi nel cestino (soft-deleted). Ripristina per riportarli in lista, oppure elimina definitivamente."
              : "Pratiche annullate. Riapri per riportarle a 'impegnata', oppure elimina definitivamente."}
          </DialogDescription>
        </DialogHeader>

        {isAdmin && items.length > 0 && !confirmEmpty && (
          <div className="flex justify-end pb-2">
            <Button variant="outline"
                    className="text-rose-600 border-rose-300 hover:bg-rose-50"
                    onClick={() => setConfirmEmpty(true)}
                    data-testid="btn-svuota-cestino">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Svuota cestino
            </Button>
          </div>
        )}

        {confirmEmpty && (
          <div className="rounded-lg border border-rose-300 bg-rose-50 p-3 mb-2 flex items-start gap-2"
               data-testid="confirm-svuota">
            <AlertTriangle className="h-4 w-4 text-rose-600 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm text-rose-900 font-semibold">
                Confermi lo svuotamento del cestino?
              </div>
              <div className="text-xs text-rose-700 mt-0.5">
                Tutti gli elementi ({items.length}) verranno eliminati definitivamente. NON reversibile.
              </div>
              <div className="flex gap-2 mt-2">
                <Button size="sm" variant="outline"
                        onClick={() => setConfirmEmpty(false)}
                        disabled={busyId === "__empty__"}>
                  Annulla
                </Button>
                <Button size="sm"
                        className="bg-rose-600 hover:bg-rose-700 text-white"
                        onClick={handleSvuota}
                        disabled={busyId === "__empty__"}
                        data-testid="btn-svuota-cestino-confirm">
                  {busyId === "__empty__" ? "Eliminazione..." : "Svuota tutto"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {loading && <div className="text-center py-6 text-slate-400">Caricamento...</div>}
        {!loading && items.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <Trash2 className="h-10 w-10 mx-auto mb-2 opacity-40" />
            Cestino vuoto
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="overflow-x-auto" data-testid="cestino-list">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-[11px] uppercase text-slate-600">
                  <th className="px-2 py-2">Codice</th>
                  <th className="px-2 py-2">Veicolo</th>
                  <th className="px-2 py-2">Data {tipo === "preventivo" ? "eliminazione" : "annullamento"}</th>
                  <th className="px-2 py-2 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((it) => {
                  const dataAzione = tipo === "preventivo"
                    ? (it.deleted_at || "").slice(0, 10)
                    : (it.annullata_at || it.updated_at || "").slice(0, 10);
                  return (
                    <tr key={it.id} className="hover:bg-slate-50" data-testid={`cestino-row-${it.id}`}>
                      <td className="px-2 py-2 font-mono text-[12px]">{it.codice}</td>
                      <td className="px-2 py-2">
                        <span className="font-medium">
                          {it.vehicle_snapshot?.marca} {it.vehicle_snapshot?.modello}
                        </span>
                        {it.vehicle_snapshot?.targa && (
                          <span className="text-[11px] text-slate-500 ml-1">({it.vehicle_snapshot.targa})</span>
                        )}
                      </td>
                      <td className="px-2 py-2 font-mono text-[12px] text-slate-600">{dataAzione || "—"}</td>
                      <td className="px-2 py-2 text-right">
                        <div className="inline-flex gap-1">
                          <Button size="sm" variant="outline"
                                  onClick={() => handleRipristina(it)}
                                  disabled={busyId === it.id}
                                  data-testid={`btn-ripristina-${it.id}`}>
                            <RotateCcw className="h-3 w-3 mr-1" />
                            {tipo === "pratica" && it.status === "annullata" ? "Riapri" : "Ripristina"}
                          </Button>
                          {isAdmin && (
                            <Button size="sm" variant="outline"
                                    className="text-rose-600 border-rose-300 hover:bg-rose-50"
                                    onClick={() => handlePermanentDelete(it)}
                                    disabled={busyId === it.id}
                                    data-testid={`btn-permanent-${it.id}`}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Chiudi</Button>
        </DialogFooter>
      </DialogContent>
      <RiapriPraticaDialog open={!!riapri} onOpenChange={(o) => { if (!o) setRiapri(null); }}
        vendita={riapri} motivo="Riapertura da cestino" onDone={() => { load(); onChange?.(); }} />
    </Dialog>
  );
}
